import type { ReactElement, ReactNode } from 'react'

/**
 * What a surface shows when there is genuinely nothing — no sessions, no
 * pending approvals, no audit rows for the filter.
 *
 * Deliberately not a sad illustration. An empty approvals queue is good
 * news in an operator console, and the useful thing to put on screen is
 * the one action that would change the state, not an apology.
 *
 * `DataTable` renders this inside its own body so the column headers stay
 * put; this component only owns the content.
 */

export interface EmptyStateProps {
  /** A glyph or small SVG. Decorative — the headline carries the meaning,
   * so this is wrapped `aria-hidden`. */
  icon?: ReactNode
  headline: string
  description?: ReactNode
  /** One control. More than one means the screen has not decided what the
   * operator should do next. */
  action?: ReactNode
}

export function EmptyState({
  icon,
  headline,
  description,
  action,
}: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      {icon !== undefined && (
        <div aria-hidden="true" className="text-faint">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-fg">{headline}</p>
      {description !== undefined && (
        <p className="max-w-prose text-xs text-muted">{description}</p>
      )}
      {action !== undefined && <div className="mt-1">{action}</div>}
    </div>
  )
}
